import React from 'react';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useActiveLeadStatuses } from '@/api/lead-statuses/use-lead-statuses';
import { useUsers } from '@/api/users/use-users';

/** Sentinel values mirroring the import wizard's "no selection" options. */
export const NO_DEFAULT_STATUS_VALUE = '__no_status__';
export const NO_DEFAULT_ASSIGNEE_VALUE = '__no_assignee__';

export interface LeadDefaultsValue {
    leadStatusId: string;
    assigneeId: string;
}

export interface LeadDefaultsStepProps {
    value: LeadDefaultsValue;
    onChange: (value: LeadDefaultsValue) => void;
}

/**
 * Default lead status + default assignee selectors, extracted from the
 * import wizard's "Import Options" step so the Campaign editor (W3) can
 * reuse the same controls. Loads statuses/users the same way the wizard did
 * (react-query hooks, cached by query key) rather than taking the lists as
 * props.
 */
const LeadDefaultsStep: React.FC<LeadDefaultsStepProps> = ({ value, onChange }) => {
    const { data: leadStatusesData } = useActiveLeadStatuses();
    const { data: usersData } = useUsers();

    return (
        <>
            <div className="space-y-2">
                <Label htmlFor="lead-status">Default Lead Status</Label>
                <Select
                    value={value.leadStatusId}
                    onValueChange={leadStatusId => onChange({ ...value, leadStatusId })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select a lead status (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NO_DEFAULT_STATUS_VALUE}>No default status</SelectItem>
                        {leadStatusesData?.data?.map(status => (
                            <SelectItem key={status.id} value={status.id}>
                                {status.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                    All imported leads will be assigned this status. Leave empty to use system
                    default.
                </p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="assignee">Default Assignee</Label>
                <Select
                    value={value.assigneeId}
                    onValueChange={assigneeId => onChange({ ...value, assigneeId })}
                >
                    <SelectTrigger>
                        <SelectValue placeholder="Select an assignee (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value={NO_DEFAULT_ASSIGNEE_VALUE}>
                            No default assignee
                        </SelectItem>
                        {usersData?.data?.map(user => (
                            <SelectItem key={user.id} value={user.id}>
                                {user.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                    All imported leads will be assigned to this user. Leave empty to not assign.
                </p>
            </div>
        </>
    );
};

export default LeadDefaultsStep;
